/* eslint-disable no-console */

import Backbone from 'backbone';
import CustomizationsCollection from 'ef/collections/customizations_collection';
import ResourcesCollection from 'ef/collections/resources_collection';
import ResponseSetCollection from 'ef/collections/evaluations/response_set_collection';
import ActivityProgressesCollection from 'ef/collections/activity_progresses_collection';
import ModulesCollection from 'ef/collections/modules_collection';
import GroupCollection from 'ef/collections/group_collection';
import Program from 'ef/models/program';
import ProgressQueue from 'ef/models/progress_queue';
import Group from 'ef/models/group';
import _ from 'lodash';
import $ from 'jquery';
import ContentProvider from 'services/content-provider';
import { LocalStorageService } from 'services/data-handler-services/web-storage-service';

import UrlHelper from 'lib/url-helper';

// # EverFi's Content Partner SDK.
//
// Dependencies: jquery/backbone/underscore
//
//
// This file provides the top level namespace `EF` for all of our models,
// collections and controllers to be attached to and for the main methods of
// the API to live.
//
//
//
// Decorate EF namespace with our stuff.
const EF = {
  version: '0.5.8',
  // ### EF.init
  //
  // the init function sets up the API with the information it needs to operate.
  // this method takes 3 arguments:
  //   `EF.Models.Enrollment` Object
  //   `EF.Models.Module` Object
  //   `options` object. Includes a 'stage' key, which will be passed along to
  //      the page objects when they are initialized and signifies the location
  //      of the HTML on the page. Options can also have a 'apiOnly' key which
  //      should be a boolean value and will signify only using the
  //      SDK in api only mode, which doesn't play pages for you.
  //
  options: { local: true },
  init: async function (groupsJSON, modulesJSON, modulesContentJson, enrollment_hash, options, config) {
    const self = this;
    // ContentProvider requires CourseSettings to be initialized to instantiate
    // which happens before this 'init' call in ef_sdk
    self.ContentProvider = ContentProvider.initialize();

    const defaults = {
      apiOnly: false,
      local: true,
      autoNextActivity: false,
      loadActivityProgresses: true,
      pmMode: false,
      roboLog: false,
      saveLater: false,
      scriptFolder: 'scripts',
      templateFolder: 'templates',
      cssFolder: 'css',
    };
    this.urlHelper = this.urlHelper || new UrlHelper();
    this.groupsJSON = groupsJSON;
    this.modulesJSON = modulesJSON;
    this.config = this.urlHelper.buildConfig(config);
    if (config.accessToken) {
      Backbone.ajax = function () {
        if (!arguments[0].headers) {
          arguments[0].headers = {
            Authorization: `Bearer ${config.accessToken}`,
          };
        } else if (!arguments[0].headers.Authorization) {
          arguments[0].headers.Authorization = `Bearer ${config.accessToken}`;
        }
        if (arguments[0].xhrFields === undefined || arguments[0].xhrFields.withCredentials === undefined) {
          arguments[0].xhrFields = arguments[0].xhrFields || {};
          arguments[0].xhrFields.withCredentials = true;
        }
        if (config.portalQueryString) {
          arguments[0].url = `${arguments[0].url}${arguments[0].url.includes('?') ? '&' : '?'}${
            config.portalQueryString
          }`;
        }
        return Backbone.$.ajax.apply(Backbone.$, arguments);
      };
    }
    this.options = _.defaults(this.urlHelper.buildOptions(options), defaults);
    this.enrollment = this.urlHelper.buildEnrollment(enrollment_hash);
    this.customizations = new CustomizationsCollection([], {
      enrollment: this.enrollment,
    });
    this.resources = new ResourcesCollection([], {
      enrollment: this.enrollment,
    });

    this.stage = this.config.get('stage');
    this.language = 'en';
    this.program = new Program([], { enrollment: this.enrollment });

    this.progressQueue = new ProgressQueue();

    // load here?
    const preloads = [
      self.ContentProvider.InitializationService.preload(),
      EF.ContentProvider.ResourceService.loadResources(),
    ];
    await Promise.all(preloads);
    this.checkForgeMode();
    this.setGroupsByJSON(this.groupsJSON);
    this.setModulesByJSON(this.modulesJSON, modulesContentJson, function () {
      self.attachCompletionEvents();

      const loads = self.ContentProvider.InitializationService.load();

      if (self.options.evaluations) {
        const evaluations = $.map(self.options.evaluations, function (value) {
          return [value];
        });
        EF.responseSet = new ResponseSetCollection(evaluations);
      }

      if (self.options.loadActivityProgresses) {
        EF.activityProgresses = new ActivityProgressesCollection();
        if (!self.options.local) {
          loads.push(EF.activityProgresses.fetch());
        }
      }

      if (self.options.local) {
        EF.modules.each(m => m.fetch());

        const skipping = LocalStorageService.getItem('pageSkipping');

        EF.options.pageSkipping = skipping === undefined || skipping === null ? true : skipping === 'true';
      }
      $.when.apply(this, loads).done(function () {
        if (self.options.loadActivityProgresses) {
          EF.modules.syncActivityAndPageProgress(EF.activityProgresses);
        } else {
          // For content-service
          EF.modules.syncModuleProgress();
        }
        EF.trigger('ready');
        EF.modules.loaded = true;
        if (EF.router) {
          Backbone.history.start();
          EF.trigger('routerReady');
        }
      });
    });
  },

  Models: {
    Assessments: {},
    Evaluations: {},
  },

  Controllers: {},

  Collections: {
    Assessments: {},
  },

  //### registeredPages

  // registeredPages is where pages following the perscribed method of formatting
  // the page javascripts have their objects stored for retrieval.
  registeredPages: {},

  checkForgeMode: function () {
    this.forgeMode = false;
    const editModeStr = new URLSearchParams(window.location.href.toLowerCase().split('?')[1]).get('editmode');
    const isLocal = window.location.href.toLowerCase().split('/')[2] = 'http://localhost/';
    const hasQueryStr = editModeStr === 'true';
    if (navigator?.userAgent?.indexOf('Electron') >= 0 || (hasQueryStr && isLocal)) {
      this.forgeMode = true;
      this.editMode = hasQueryStr;
    }
  },

  attachCompletionEvents: function () {
    this.on('contentPage:complete', function () {
      //do nothing
    });

    this.on('contentPage:loaded', function (contentPage) {
      contentPage.trigger('loaded');
    });

    this.on('module:progress:set', function () {
      //do nothing
    });

    this.on('page:progress:set', function (contentPage) {
      contentPage.trigger('progress:set');
    });

    this.on('page:progress:saved', function () {
      if (EF.modules.every(module => module.get('completed') || module.get('skip_activity_progress'))) {
        const data = {
          event_type: 'content-complete',
        };
        window.postMessage(data, window.location.origin);
      }
    });
  },

  //returns and sets EF.module by moduleKey
  setModuleByKey: function (moduleKey) {
    this.module = this.modules.getModuleByKey(moduleKey);
    return this.module;
  },

  setModules: function (modules) {
    const options = {
      parse: true,
    };
    this.modules = new ModulesCollection(modules, options);
  },

  setGroups: function (groups) {
    this.groups = new GroupCollection(groups);
  },

  setModulesByJSON: function (modulesJSON, modulesContentJson, callback) {
    const self = this;
    // var moduleObjects = _.map(modulesJSON, function(mod, i) {
    //   if(!mod.index) { mod.index = i; }
    //   return new EF.Models.Module(mod);
    // });

    // Initialize an empty modules collection. This collection
    // will now be responsible for loading the progress.
    this.setModules(null);

    // load customizations first to get injected or replacement pages
    EF.ContentProvider.CustomizationService.load().then(() => {
      self.ContentProvider.InitializationService.applyTransformations(
        modulesJSON,
        modulesContentJson,
        this.groupsJSON
        // eslint-disable-next-line no-unused-vars
      ).then(([modulesJSON, modulesContentJson, groupsJSON]) => {
        this.modulesJSON = modulesJSON;
        this.groupsJSON = groupsJSON;

        // Store the module JSON on the collection, will be used in the parse
        // method to translate module_statuses to page level progress.
        this.modules.content_modules = modulesJSON;

        this.modules.fetch({
          success: callback,
          error: this.loadError,
        });
      });
    });
  },

  setGroupsByJSON: function (groupsJSON) {
    const groupObjects = _.map(groupsJSON, function (group) {
      return new Group(group);
    });
    this.setGroups(groupObjects);
  },

  url: function (path) {
    return EF.config.get('basePath') + path;
  },

  surveyUrl: function (path) {
    return EF.config.get('assessmentEndpoint') + path;
  },

  JSON: {
    parse: function (str) {
      if (typeof str === 'undefined') {
        return str;
      }
      try {
        return JSON.parse(str);
      } catch (e) {
        console.log('EF.enrollment: could not parse JSON string ', str, 'Assuming it is a string. Error: ', e); //eslint-disable-line no-console
        return str;
      }
    },
    stringify: function (value) {
      // JSON.stringify('off') yields "off" with quotes around it.
      // Leave the value alone if it is a primitive
      return Object(value) === value ? JSON.stringify(value) : value;
    },
  },
};

// Can set config attributes but can't change them
// ## EF.Config
//
// This is an object for setting and retrieving configuration options for a
// use within the API. For example, set the survey endpoint to be used.
let configAttributes;
EF.Config = function (config) {
  const defaultAttributes = {
    basePath: 'https://api.everfi.net',
    assessmentEndpoint: 'https://surveys.everfi.net',
    webChatBasePath: 'https://apps.mypurecloud.com/widgets/9.0',
  };
  configAttributes = _.defaults(config, defaultAttributes);
};

EF.Config.prototype = {
  // retrieve a config value by key
  get: function (key) {
    return configAttributes[key];
  },

  getAll: function () {
    return configAttributes;
  },
};

_.extend(EF, Backbone.Events);

export default EF;
